import { PostHog } from "posthog-node";
import { getPostHogClient } from "./posthog.js";
import { ConfigProvider } from "../config/provider.js";
import { AppConfig } from "../config/schemas.js";
import { logger } from "../logger.js";

export interface AnalyticsProviderImpl {
  capture(event: string, properties?: Record<string, any>): Promise<void>;
  captureException(
    error: unknown,
    properties?: Record<string, any>,
  ): Promise<void>;
  close(): Promise<void>;
}

export class AnalyticsProvider implements AnalyticsProviderImpl {
  private configProvider: ConfigProvider;
  private posthog: PostHog;

  constructor(params: {
    appConfig: AppConfig;
    configProvider: ConfigProvider;
  }) {
    this.configProvider = params.configProvider;
    this.posthog = getPostHogClient(params.appConfig.posthog.apiKey);
    logger.debug("AnalyticsProvider initialized");
  }

  private async getUserId() {
    return (await this.configProvider.getConfig()).identity?.user.id;
  }

  private async getApiUrl() {
    return (await this.configProvider.getConfig()).api.url;
  }

  private async extendProperties(properties?: Record<string, any>) {
    return {
      ...properties,
      // version: getPackageVersion(), // TODO: Add this back in
      // "sdk-version": getSDKVersion(), // TODO: Add this back in
      "api-url": await this.getApiUrl(),
    };
  }

  async capture(event: string, properties?: Record<string, any>) {
    const userId = await this.getUserId();
    if (!userId) {
      logger.warn(
        `No User ID found, skipping analytics capture for event: ${event}`,
      );
      return;
    }

    this.posthog.capture({
      distinctId: userId,
      event: event,
      properties: this.extendProperties(properties),
    });
    logger.debug(`Captured event: ${event} for user: ${userId}`);
  }

  async captureException(error: unknown, properties?: Record<string, any>) {
    const userId = await this.getUserId();
    if (!userId) {
      logger.warn(
        `No User ID found, skipping analytics capture for exception: ${error}`,
      );
      return;
    }

    this.posthog.captureException(
      error,
      userId,
      this.extendProperties(properties),
    );
    logger.debug(`Captured exception: ${error}`);
  }

  async close() {
    await this.posthog.shutdown();
    logger.debug("AnalyticsProvider closed");
  }
}

/**
 * A dummy analytics provider that does nothing.
 * This is used when the user is in local mode.
 */
export class DummyAnalyticsProvider implements AnalyticsProviderImpl {
  private configProvider: ConfigProvider;

  constructor(params: {
    appConfig: AppConfig;
    configProvider: ConfigProvider;
  }) {
    this.configProvider = params.configProvider;
  }

  private async getUserId() {
    return (await this.configProvider.getConfig()).identity?.user.id;
  }

  async capture(event: string, _?: Record<string, any>) {
    logger.debug(
      `DummyAnalyticsProvider: Captured event: ${event} for user: ${await this.getUserId()}`,
    );
  }

  async captureException(error: unknown, _?: Record<string, any>) {
    logger.debug(`DummyAnalyticsProvider: Captured exception: ${error}`);
  }

  async close() {
    logger.debug("DummyAnalyticsProvider closed");
  }
}
